<script lang="ts" setup>
import { Theme } from '@/composition/themes'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { GroupMemberRole } from '@/models/GroupMember'
import {
  UIAvatar,
  UIBadge,
  UITextMdMedium,
  UITextMdNormal,
  UITextSmMedium,
} from '@gohighlevel/ghl-ui'
import { PropType, computed } from 'vue'
import { getShortName } from '../../helper'
import ShieldIcon from '../icons/ShieldIcon.vue'

const props = defineProps({
  avatarUrl: {
    type: String,
    required: false,
  },
  fullName: {
    type: String,
    required: false,
  },
  size: {
    type: [String, Number] as PropType<number | 'small' | 'medium' | 'large'>,
    default: 'large',
  },
  showBadge: {
    type: Boolean,
    default: false,
  },
  role: {
    type: String as PropType<GroupMemberRole>,
    required: false,
  },
  level: {
    type: String,
    default: '1',
  },
  border: {
    type: Boolean,
    default: true,
    required: false,
  },
  showOnlineStatus: {
    type: Boolean,
    default: false,
    required: false,
  },
})
const avatarSize = computed(() => {
  if (props.size === 'small') {
    return 28
  } else if (props.size === 'medium') {
    return 34
  } else if (props.size === 'large') {
    return 40
  }
  return props.size
})

const badgeSize = computed(() => {
  if (avatarSize.value == 40) {
    return 20
  } else if (avatarSize.value == 48) {
    return 20
  } else if (avatarSize.value == 80) {
    return 24
  } else if (avatarSize.value == 158) {
    return 42
  } else if (avatarSize.value == 152) {
    return 38
  } else if (avatarSize.value == 130) {
    return 33
  } else {
    return avatarSize.value * 0.4
  }
})

const avatarTextSize = computed(() => {
  if (avatarSize.value == 158 || avatarSize.value == 152) {
    return 50
  } else if (avatarSize.value == 64) {
    return 20
  } else if (avatarSize.value == 80) {
    return 30
  } else if (avatarSize.value == 48) {
    return 18
  } else if (avatarSize.value == 40) {
    return 16
  } else if (avatarSize.value == 25 || avatarSize.value == 12) {
    return 12
  } else {
    return 15
  }
})

const badgeTextSize = computed(() => {
  if (badgeSize.value == 42) {
    return 20
  } else if (badgeSize.value == 38) {
    return 16
  } else if (badgeSize.value == 18) {
    return 10
  } else if (badgeSize.value == 24) {
    return 12
  } else if (badgeSize.value == 20) {
    return 10
  } else {
    return 14
  }
})

const badgeXoffset = computed(() => {
  if (avatarSize.value === 158) {
    return -16
  } else {
    return (avatarSize.value / 32) * -3.5
  }
})

const badgeYoffset = computed(() => {
  if (avatarSize.value === 158) {
    return 140
  } else {
    return (avatarSize.value / 32) * 27 - (7 * 32) / avatarSize.value + 4
  }
})

const sanitizedFullName = computed(() => {
  return getShortName(props.fullName || 'Deleted User')
})
</script>
<template>
  <UIBadge
    :id="'avatar-badge'"
    class="custom-badge-sup"
    :offset="[badgeXoffset, badgeYoffset]"
    :color="'bg - transparent'"
    :show="showBadge"
  >
    <template #content>
      <UIAvatar
        v-if="avatarUrl"
        :size="size"
        :src="replaceBucketUrlWithCdnUrl(avatarUrl)"
        :class="border ? '!border-[0.75px]' : '!border-[0px]'"
        style="
           {
            border: 0px solid var(--border) !important;
          }
        "
      />
      <UIAvatar
        v-else
        :size="size"
        class="text-md flex items-center justify-center bg-communities-fill font-medium text-communities-font-secondary"
      >
        <UITextSmMedium
          v-sanitized-html="sanitizedFullName"
          :style="{ 'font-size': `${avatarTextSize}px !important` }"
        />
      </UIAvatar>
    </template>
    <template #value>
      <div
        v-if="showOnlineStatus"
        class="relative h-3 w-3 rounded-full bg-green-500"
      />
      <div
        v-else-if="
          role === GroupMemberRole.ADMIN || role === GroupMemberRole.OWNER
        "
        class="relative"
      >
        <ShieldIcon
          :class="
            Theme.state.isDarkMode
              ? 'fill-communities-primary-light'
              : 'fill-communities-primary-dark'
          "
          :style="{ width: `${badgeSize}px`, height: `${badgeSize}px` }"
        />
        <UITextMdNormal
          :style="{ 'font-size': `${badgeTextSize}px !important` }"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%] transform text-communities-sidebar-selected-item-font"
          >{{ level }}</UITextMdNormal
        >
      </div>
      <div
        v-else
        :style="{ width: `${badgeSize}px`, height: `${badgeSize}px` }"
        :class="
          Theme.state.isDarkMode
            ? 'bg-communities-primary-light'
            : 'bg-communities-primary-dark'
        "
        class="flex items-center justify-center rounded-full border border-solid border-communities-border pb-[1px] pl-[1px] pr-[0.5px] pt-[0.5px] text-communities-border"
      >
        <UITextMdMedium
          :style="{ 'font-size': `${badgeTextSize}px !important` }"
          class="text-communities-sidebar-selected-item-font"
          >{{ level }}</UITextMdMedium
        >
      </div>
    </template>
  </UIBadge>
</template>
